<script setup lang="ts">
import { computed, useClock, useDayjs, useCoreConfig, useRoute, onMounted, useFormat, useIntercom, until, useOpeningHours, useCookiebotConsent, navigateTo, useI18n } from '#imports'

export interface OpeningHour {
    date: string
    is_today: boolean
    is_open: boolean | null
    hours: Array<any>
    from: Date | number | undefined
    to: Date | number | undefined
}

export interface SimpleOpeningHours {
    openingHours: string
    language: string
}

export interface CustomerSupportContainerProps {
    openingHoursVisible?: boolean
}

const props = withDefaults(defineProps<CustomerSupportContainerProps>(), {
    openingHoursVisible: true
})

const dayjs = useDayjs()
const route = useRoute()
const { data: openingHoursData, error: unableToGetOpeningHours } = await useOpeningHours()
const { clock } = useClock()
const { asDate } = useFormat()
const { company, customerSupport, monolith } = useCoreConfig()
const { showIntercom, isBooted, boot, hasConsent, showCookieNotice, showUnavailableNotice } = useIntercom()
const { isConsentReady } = useCookiebotConsent()
const { t } = useI18n()

function isNow(openingHour: OpeningHour) {
    if ((!openingHour.from && !openingHour.to) || !openingHour.is_open) {
        return openingHour.is_today
    }

    const fromDate = dayjs.tz(`${openingHour.date} ${openingHour.from}`, monolith.timezone)
    const toDate = dayjs.tz(`${openingHour.date} ${openingHour.to}`, monolith.timezone)

    return dayjs(clock.value).tz(monolith.timezone).isBetween(fromDate, toDate)
}

const openingHours = computed(() => openingHoursData.value?.openingHours || [])
const today = computed(() => {
    const clockAsDate = asDate(clock.value, {
        format: 'YYYY-MM-DD'
    })

    return openingHours.value.length ? openingHours.value.find(el => el.date === clockAsDate) : null
})

const hasOpeningHours = computed(() => openingHours.value.length > 0)
const isOpenToday = computed(() => today.value?.from && today.value?.to)
const customerSupportPath = computed(() => t('pages.customerSupport'))
const contactModalInQuery = computed(() => !!route.query.contactModal)

async function openChat() {
    if (route.path !== customerSupportPath.value) return navigateTo(customerSupportPath.value)

    await until(isConsentReady).toBe(true)

    if (!hasConsent.value) {
        showCookieNotice.value = true

        return
    }

    if (!isBooted.value) {
        const didBoot = await boot()

        if (!didBoot) {
            showUnavailableNotice()

            return
        }
    }

    await showIntercom()
}

function isToday(dateString: string) {
    return today.value && dateString === today.value.date
}

/**
 * The results for specific language opening hours arrive in a format that's difficult to use.
 * We simplify them to an array of objects of format e.g.
 * `{ language: 'Dansk', openingHours: '15:00 - 19:00' }`
 * or
 * `{ language: 'English', openingHours: 'closed' }`
 */
function simplifySpecificLanguageOpeningHours() {
    if (!today.value) return

    const _openingHours: SimpleOpeningHours[] = []

    today.value.hours.filter(timeframe => timeframe.enabled).forEach((timeframe) => {
        const enabledLangs: Array<any> = timeframe.languages.filter((lang: any) => lang.enabled)

        enabledLangs.forEach((lang: any) => {
            const langTranslated = customerSupport?.languages ? (customerSupport.languages as Record<string, string | undefined>)[lang.language.toLowerCase()] : lang.language.toLowerCase()
            _openingHours.push({
                openingHours: timeframe.enabled ? `${timeframe.from} - ${timeframe.to}` : t('nimbus.customerSupport.closed'),
                language: langTranslated
            })
        })
    })

    return _openingHours
}

function getSpecificLanguageOpeningHours(day: OpeningHour) {
    if (!isToday(day.date)) {
        return []
    }

    if (!today.value?.is_open) {
        return []
    }

    if (!today.value?.hours?.length) {
        return []
    }

    return simplifySpecificLanguageOpeningHours()
}

onMounted(() => {
    if (contactModalInQuery.value) {
        setTimeout(() => openChat(), 2000)
    }
})
</script>

<template>
    <div class="customer-service">
        <div class="customer-service__heading">
            <h3>
                {{ t('nimbus.customerSupport.title') }}
            </h3>
        </div>

        <div class="customer-service__tile panel">
            <div class="panel__body">
                <i18n-t
                    class="customer-service__help-text"
                    keypath="nimbus.customerSupport.readBeforeContactingText"
                    tag="p"
                >
                    <template #faq>
                        <NuxtLink :to="t('pages.faq')">
                            {{ t('nimbus.customerSupport.faq') }}
                        </NuxtLink>
                    </template>
                </i18n-t>

                <h4 v-if="company?.phone" class="customer-service__phone">
                    <a
                        :href="`tel:${company.phone.raw}`"
                        data-test="a-support-phone"
                        v-text="company.phone.displayed"
                    />
                </h4>

                <div v-if="isOpenToday" class="customer-service__opening-hours" data-test="div-support-hours">
                    <p class="customer-service__opening-hours-label">
                        {{ t('nimbus.customerSupport.phoneHoursToday') }}
                    </p>

                    <p v-if="today" class="customer-service__opening-hours-time">
                        {{ today.from }} - {{ today.to }}
                    </p>
                </div>

                <div v-else class="customer-service__opening-hours" data-test="div-support-hours">
                    <p v-if="!hasOpeningHours" class="customer-service__opening-hours-label">
                        {{ t('nimbus.customerSupport.phoneHoursToday') }} <br>
                        {{ unableToGetOpeningHours }}
                    </p>
                    <p v-else class="customer-service__opening-hours-label">
                        {{ t('nimbus.customerSupport.phoneIsClosed') }}
                    </p>
                </div>
            </div>

            <div class="customer-service__footer panel__footer">
                <button
                    data-test="button-open-support-modal"
                    @click.prevent="openChat()"
                >
                    {{ t('nimbus.customerSupport.dialogTitle') }}
                </button>
            </div>
        </div>

        <div v-if="openingHoursVisible && hasOpeningHours" class="customer-service__phone-times">
            <h3>
                {{ t('nimbus.customerSupport.phoneHoursTitle') }}
            </h3>
            <div class="customer-service__tile panel">
                <table class="customer-service__table" data-test="table-support-hours">
                    <tbody>
                        <template v-for="day in openingHours" :key="day.date">
                            <tr :class="{ 'customer-service__cell--highlight': isNow(day) }">
                                <td>
                                    {{ dayjs.tz(day.date, monolith.timezone).format('dddd DD/MM') }}
                                </td>
                                <td v-if="!day.is_today && !day.is_open">
                                    {{ t('nimbus.customerSupport.closed') }}
                                </td>
                                <td v-else>
                                    {{ day.from }} - {{ day.to }}
                                </td>
                            </tr>

                            <tr
                                v-for="hours in getSpecificLanguageOpeningHours(day)"
                                :key="`${hours.language}_${day.date}`"
                                class="customer-service__cell--additional"
                            >
                                <td>{{ hours.language }}</td>
                                <td>{{ hours.openingHours }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.customer-service {
    &__heading {
        display: flex;
        flex-direction: row;

        h3 {
            margin-right: 15px;
        }
    }

    &__tile {
        text-align: center;
    }

    &__phone {
        margin: 0;
        color: $brand-success;
        font-weight: 700;

        a {
            color: inherit;
            text-decoration: none;
        }
    }

    &__opening-hours-label {
        margin: 0;
    }

    &__opening-hours-time {
        margin: 0;
        font-weight: $text-bold;
    }

    &__table {
        /* Hack that fixes a white vertical bar on Chromium browsers (including Chrome) on certain resolutions */
        width: calc(100% + 0.5px);
        overflow: hidden;
        border-radius: var(--border-radius, $border-radius);
        font-size: $font-size-base;

        @media (min-width: $screen-sm) {
            font-size: $font-size-xs;
        }

        & > tbody > tr:not(.customer-service__cell--additional) {
            &:not(:first-child) {
                border-top: 1px solid $panel-border-color;
            }
        }

        & > tbody > tr:not(.customer-service__cell--additional) > td {
            padding: 8px;

            &::first-letter {
                text-transform: capitalize;
            }

            &:first-child {
                text-align: left;
            }

            &:last-child {
                text-align: right;
            }
        }
    }

    &__cell--highlight {
        color: $panel-highlight-font-color;
        background-color: $panel-highlight-bg;
    }

    &__footer {
        border-radius: 0 0 var(--border-radius, $border-radius) var(--border-radius, $border-radius);

        button {
            border: none;
            color: $brand-primary;
            background: transparent;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__contact-info {
        margin-bottom: 20px;
        padding: 19px;
        border: 1px solid $panel-border-color;
        border-radius: $border-radius;
        background-color: $panel-footer-color;
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.05);

        address {
            margin-bottom: 0;
            color: $subtle-text-color;
            font-size: $font-size-sm;

            @media (min-width: $screen-sm) {
                font-size: $font-size-base;
            }

            @media (min-width: $screen-md) {
                font-size: $font-size-sm;
            }
        }
    }
}

.customer-service__cell--additional {
    background-color: $panel-border-color;

    & > td {
        padding: 5px 8px;
        font-size: 0.6rem;

        &::first-letter {
            text-transform: capitalize;
        }

        &:first-child {
            text-align: left;
        }

        &:last-child {
            text-align: right;
        }

        &.closed {
            width: 100%;
            text-align: center;
        }
    }
}
</style>
